import { COLORS } from '@hollowveil/shared';
import type { ModelProps, ModelRegistry } from './types';

export function CrystalModel({ world, hovered }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  return (
    <mesh position={[0, 0.5, 0]} castShadow>
      <octahedronGeometry args={[0.4]} />
      <meshStandardMaterial
        color={world === 'real' ? colors.gold : colors.glow}
        emissive={world === 'real' ? colors.gold : colors.glow}
        emissiveIntensity={hovered ? 0.5 : 0.2}
        roughness={0.2}
        metalness={0.8}
      />
    </mesh>
  );
}

export function OrbModel({ world, hovered }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  return (
    <mesh position={[0, 0.5, 0]} castShadow>
      <sphereGeometry args={[0.3, 32, 32]} />
      <meshStandardMaterial
        color={world === 'real' ? colors.accent : colors.ethereal}
        emissive={world === 'spectral' ? colors.glow : undefined}
        emissiveIntensity={world === 'spectral' ? 0.3 : 0}
        roughness={0.1}
        metalness={0.3}
      />
    </mesh>
  );
}

export function SpectralKeyModel({ hovered }: ModelProps) {
  return (
    <group position={[0, 0.5, 0]} rotation={[0, 0, Math.PI / 4]}>
      {/* Key handle (ring) */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <torusGeometry args={[0.12, 0.03, 8, 16]} />
        <meshStandardMaterial
          color="#6a5acd"
          emissive="#8866ff"
          emissiveIntensity={0.5}
          roughness={0.2}
          metalness={0.8}
          transparent
          opacity={0.9}
        />
      </mesh>
      {/* Key shaft */}
      <mesh position={[0, -0.05, 0]} castShadow>
        <boxGeometry args={[0.04, 0.35, 0.02]} />
        <meshStandardMaterial
          color="#8877dd"
          emissive="#aa99ff"
          emissiveIntensity={0.4}
          roughness={0.3}
          metalness={0.7}
        />
      </mesh>
      {/* Key teeth */}
      <mesh position={[0.04, -0.18, 0]} castShadow>
        <boxGeometry args={[0.06, 0.04, 0.02]} />
        <meshStandardMaterial
          color="#8877dd"
          emissive="#aa99ff"
          emissiveIntensity={0.4}
          roughness={0.3}
          metalness={0.7}
        />
      </mesh>
      <mesh position={[0.035, -0.23, 0]} castShadow>
        <boxGeometry args={[0.05, 0.03, 0.02]} />
        <meshStandardMaterial
          color="#8877dd"
          emissive="#aa99ff"
          emissiveIntensity={0.4}
          roughness={0.3}
          metalness={0.7}
        />
      </mesh>
      {/* Glow orb at center of handle */}
      <mesh position={[0, 0.25, 0]}>
        <sphereGeometry args={[0.05, 12, 12]} />
        <meshStandardMaterial
          color="#cc99ff"
          emissive="#dd88ff"
          emissiveIntensity={hovered ? 1.2 : 0.8}
          transparent
          opacity={0.8}
        />
      </mesh>
    </group>
  );
}

export function MagicFlowerModel({ hovered }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {/* Stem */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.025, 0.6, 8]} />
        <meshStandardMaterial
          color="#2a5a2a"
          emissive="#3a7a3a"
          emissiveIntensity={0.2}
          roughness={0.6}
        />
      </mesh>
      {/* Leaves on stem */}
      <mesh position={[0.08, 0.2, 0]} rotation={[0, 0, -0.5]} castShadow>
        <coneGeometry args={[0.06, 0.15, 4]} />
        <meshStandardMaterial
          color="#3a7a3a"
          emissive="#4a9a4a"
          emissiveIntensity={0.3}
        />
      </mesh>
      <mesh position={[-0.07, 0.3, 0]} rotation={[0, Math.PI, 0.5]} castShadow>
        <coneGeometry args={[0.05, 0.12, 4]} />
        <meshStandardMaterial
          color="#3a7a3a"
          emissive="#4a9a4a"
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Flower petals - arranged in a circle */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        const petalColor = i % 2 === 0 ? '#ff66aa' : '#ff88cc';
        const emissiveColor = i % 2 === 0 ? '#ff44aa' : '#ff66bb';
        return (
          <mesh
            key={i}
            position={[
              Math.cos(angle) * 0.1,
              0.6,
              Math.sin(angle) * 0.1,
            ]}
            rotation={[0.3, angle, 0.2]}
            castShadow
          >
            <coneGeometry args={[0.06, 0.15, 6]} />
            <meshStandardMaterial
              color={petalColor}
              emissive={emissiveColor}
              emissiveIntensity={hovered ? 0.6 : 0.3}
              roughness={0.4}
            />
          </mesh>
        );
      })}
      {/* Center of flower (pistil) */}
      <mesh position={[0, 0.62, 0]} castShadow>
        <sphereGeometry args={[0.06, 12, 12]} />
        <meshStandardMaterial
          color="#ffdd44"
          emissive="#ffaa00"
          emissiveIntensity={hovered ? 0.8 : 0.5}
          roughness={0.3}
        />
      </mesh>
      {/* Magical particles around flower */}
      {[0, 1, 2].map((i) => (
        <mesh
          key={`particle-${i}`}
          position={[
            Math.cos((i / 3) * Math.PI * 2) * 0.2,
            0.7 + i * 0.05,
            Math.sin((i / 3) * Math.PI * 2) * 0.2,
          ]}
        >
          <sphereGeometry args={[0.015, 6, 6]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive="#aaffaa"
            emissiveIntensity={1}
            transparent
            opacity={0.7}
          />
        </mesh>
      ))}
    </group>
  );
}

export function AncientSeedModel({ hovered }: ModelProps) {
  return (
    <group position={[0, 0.3, 0]}>
      {/* Main seed body - glowing acorn-like shape */}
      <mesh position={[0, 0, 0]} castShadow>
        <sphereGeometry args={[0.15, 12, 12]} />
        <meshStandardMaterial
          color="#4a2a0a"
          emissive="#886622"
          emissiveIntensity={hovered ? 0.8 : 0.4}
          roughness={0.4}
        />
      </mesh>
      {/* Seed cap */}
      <mesh position={[0, 0.12, 0]} castShadow>
        <sphereGeometry args={[0.12, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color="#2a4a2a"
          emissive="#3a6a3a"
          emissiveIntensity={hovered ? 0.6 : 0.3}
          roughness={0.5}
        />
      </mesh>
      {/* Stem on top */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 0.08, 6]} />
        <meshStandardMaterial
          color="#3a5a2a"
          emissive="#4a7a3a"
          emissiveIntensity={0.2}
        />
      </mesh>
      {/* Inner glow core */}
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial
          color="#ffcc66"
          emissive="#ffaa33"
          emissiveIntensity={hovered ? 1.5 : 0.8}
          transparent
          opacity={0.6}
        />
      </mesh>
      {/* Magical energy particles */}
      {[0, 1, 2, 3].map((i) => {
        const angle = (i / 4) * Math.PI * 2;
        return (
          <mesh
            key={i}
            position={[
              Math.cos(angle) * 0.25,
              0.1 + Math.sin(i * 1.5) * 0.1,
              Math.sin(angle) * 0.25,
            ]}
          >
            <sphereGeometry args={[0.025, 6, 6]} />
            <meshStandardMaterial
              color="#aaffaa"
              emissive="#66ff66"
              emissiveIntensity={1.2}
              transparent
              opacity={0.8}
            />
          </mesh>
        );
      })}
    </group>
  );
}

export const collectibleModels: ModelRegistry = {
  crystal: CrystalModel,
  orb: OrbModel,
  spectral_key: SpectralKeyModel,
  magic_flower: MagicFlowerModel,
  ancient_seed: AncientSeedModel,
};
